/**
 * Put a baked GLB in the scene as one new object: the create half of
 * build_mesh, shared with build_spatial's bake(). Headless (bench/MCP) has no
 * <trace-viewer> to import through, so the bytes are persisted and stamped as
 * `src`; in the editor they go into the scene's folder and are imported, then
 * moved under `parent` with parent-local placement left to the caller.
 */
import { dispatchCatalogImport } from '../catalog-import';
import { importBlobIntoSceneFolder, registerFolderFileName } from '../cms/import-into-folder';
import type { SceneObject } from 'trace-viewer/scene';
import { topLevelParent } from 'trace-viewer/editor';
import type { ToolContext } from './agent-helpers';
import { persistGeneratedBinary } from './tools/persist-generated';
import { unusedMeshAssetName } from './unused-mesh-asset-name';
import type { TrackBuildObject } from 'trace-viewer/build';
import { trackImport } from './track-import';

export async function addBakedMeshObject(ctx: ToolContext, glb: Blob, name: string, assetName: string, parent: SceneObject | null, track?: TrackBuildObject): Promise<SceneObject> {
    const viewer = ctx.viewer;
    if (!viewer?.import) {
        const relSrc = await persistGeneratedBinary(ctx, glb, 'glb');
        return trackImport(ctx, track,
            () => ctx.traceScene.createSceneObject(name, parent ?? topLevelParent(ctx.traceScene) ?? undefined, relSrc),
            object => object);
    }
    const fileName = unusedMeshAssetName(ctx.traceScene.resources, assetName);
    const folderImport = await importBlobIntoSceneFolder(glb, fileName, ctx.traceScene);
    const { sceneObject } = await trackImport(ctx, track,
        () => viewer.import(folderImport.file, folderImport.file.name, name), loaded => loaded.sceneObject);
    registerFolderFileName(ctx.traceScene, folderImport);
    if (parent) ctx.traceScene.reparentSceneObject(sceneObject, parent);
    await dispatchCatalogImport(sceneObject, parent ?? undefined);
    return sceneObject;
}
